import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import moment from 'moment';
import Tw_Cen_MT_Negrita from '../../../../componentes/font/Tw_Cen_MT/TCB_____.TTF';
import Tw_Cen_MT from '../../../../componentes/font/Tw_Cen_MT/TCM_____.TTF';
import NumeroALetras from '../../../../componentes/funciones/NumeroALetras';
import { NegocioConfig } from "../../../../configuracion/Empresa";
import { formatoMoneda, formatoNumero } from "../../../../configuracion/validaciones";

Font.register({ family: 'Tw_Cen_MT', src: Tw_Cen_MT });
Font.register({ family: 'Tw_Cen_MT_Negrita', src: Tw_Cen_MT_Negrita });

const styles = StyleSheet.create({
    page: {
        fontFamily: 'Tw_Cen_MT',
        fontSize: 10,
        paddingTop: 30,
        paddingBottom: 50,
        paddingHorizontal: 40,
        backgroundColor: '#ffffff',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 10,
    },
    companyInfoLeft: { flexGrow: 1 },
    companyName: { fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 14, color: '#333' },
    companyAddress: { fontSize: 8, color: '#666' },
    logo: { width: 80, height: 80, borderRadius: 10 },

    mainTitle: {
        fontSize: 14,
        fontFamily: 'Tw_Cen_MT_Negrita',
        textAlign: 'left',
        marginTop: 10,
        textTransform: 'uppercase'
    },

    infoGrid: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderBottomWidth: 1,
        borderBottomColor: '#eee',
        paddingBottom: 10,
        marginBottom: 10,
    },
    infoColumn: { width: '48%' },
    infoLabel: { fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 9, color: '#444' },
    infoText: { fontSize: 9, color: '#555', marginBottom: 3 },

    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#b3b3b3',
        paddingVertical: 4,
        fontFamily: 'Tw_Cen_MT_Negrita',
        color: '#000',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#E0E0E0',
        paddingVertical: 5,
    },
    col1: { width: '10%', textAlign: 'center' },
    col2: { width: '25%', textAlign: 'left', paddingLeft: 5 },
    col3: { width: '20%', textAlign: 'right' },
    col4: { width: '22.5%', textAlign: 'right' },
    col5: { width: '22.5%', textAlign: 'right', paddingRight: 5 },

    datoAnulado: {
        color: 'red',
        textDecoration: 'line-through',
    },

    footerSection: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 20,
    },
    leftFooter: { width: '60%' },
    rightFooter: { width: '35%' },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 3,
    },
    saldoActualLabel: {
        fontFamily: 'Tw_Cen_MT_Negrita',
        fontSize: 11,
        borderTopWidth: 1,
        paddingTop: 5,
        marginTop: 5
    }
});

const ReporteCredito = ({ venta }) => {
    if (!venta) return null;

    const {
        numeroFactura, Cliente, Empleado, totalVenta,
        pagoVenta = [], creditos = [], facturas = [], recibos = [],
        clienteNombre, empleadoNombre, numeroFacturaDoc, numeroReciboDoc,
        creditoSaldoActual, creditoSaldo, creditoDiasMora, creditoFecha, creditoVencimiento, creditoId
    } = venta;

    const credito = creditos[0] || {
        id: creditoId || venta.creditoId,
        saldo: creditoSaldoActual || creditoSaldo || 0,
        diasMora: creditoDiasMora || 0,
        fecha: creditoFecha || venta.createdAt,
        fechaVencimiento: creditoVencimiento
    };
    const montoOriginal = parseFloat(totalVenta) || 0;
    const totalAbonadoAcumulado = parseFloat(credito.saldo || 0);
    
    const saldoPendienteActual = creditoSaldoActual != null ? parseFloat(creditoSaldoActual) : (montoOriginal - totalAbonadoAcumulado);

    const nombreEmpresaCliente = Cliente?.ClienteEmpresas?.[0]?.nombre || clienteNombre || Cliente?.nombreCompleto || "N/A";

    // Determinar si es Factura o Recibo basándose en los arreglos del JSON
    const factura = Array.isArray(facturas) && facturas.length > 0 ? facturas[0] : null;
    const recibo = Array.isArray(recibos) && recibos.length > 0 ? recibos[0] : null;

    const esFactura = facturas && facturas.length > 0;
    const esRecibo = recibos && recibos.length > 0;
    const etiquetaDocumento = esFactura ? "factura:" : esRecibo ? "recibo:" : "documento:";

    let saldoAcumulado = montoOriginal;
    const historialConSaldos = pagoVenta.map((pv) => {
        const p = pv.pago;
        const esAnulado = p.estado === 'Anulada';
        const montoPago = parseFloat(p.pago || 0);

        const saldoAnterior = saldoAcumulado;
        if (!esAnulado) {
            saldoAcumulado -= montoPago;
        }
        const saldoNuevo = saldoAcumulado;

        return {
            fecha: p.fecha,
            monto: montoPago,
            estado: p.estado,
            saldoAnterior: saldoAnterior,
            saldoActual: esAnulado ? saldoAnterior : saldoNuevo,
            metodo: p.pagotarjeta?.length > 0 ? 'Tarjeta' : p.pagotransferencia?.length > 0 ? 'Transferencia' : 'Efectivo'
        };
    });

    const totalAbonado = historialConSaldos
        .filter(p => p.estado !== 'Anulada')
        .reduce((acc, curr) => acc + curr.monto, 0);

    return (
        <Document>
            <Page size="LETTER" style={styles.page}>
                <View style={styles.header}>
                    <View style={styles.companyInfoLeft}>
                        <Text style={styles.companyName}>{NegocioConfig.nombre}</Text>
                        <Text style={styles.companyAddress}>{NegocioConfig.direccion}</Text>
                        <Text style={styles.companyAddress}>RTN: {NegocioConfig.rtn}  Email: {NegocioConfig.correo}</Text>
                        <Text style={styles.companyAddress}>Tel: {NegocioConfig.telefono}</Text>
                        <Text style={styles.mainTitle}>CRÉDITO No. {credito.id}</Text>
                    </View>
                    {NegocioConfig.logo && <Image style={styles.logo} src={NegocioConfig.logo} />}
                </View>

                <View style={styles.infoGrid}>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>Fecha de creación:</Text>
                        <Text style={styles.infoText}>{moment(credito.fecha).format('DD/MM/YYYY HH:mm')}</Text>

                        <Text style={styles.infoLabel}>Nombre del Cliente:</Text>
                        <Text style={styles.infoText}>{Cliente?.nombreCompleto}</Text>

                        {/* Etiqueta Dinámica basada en el tipo de documento */}
                        <Text style={styles.infoLabel}>No. {etiquetaDocumento}</Text>
                        <Text style={styles.infoText}>{factura?.id?factura.numeroFactura:recibo?.id?recibo.id:null}</Text>
                    </View>

                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>Fecha de vencimiento:</Text>
                        <Text style={styles.infoText}>{moment(credito.fechaVencimiento).format('DD/MM/YYYY')}</Text>

                        <Text style={styles.infoLabel}>Nombre de la Empresa:</Text>
                        <Text style={styles.infoText}>{nombreEmpresaCliente}</Text>

                        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                            <View>
                                <Text style={styles.infoLabel}>Total {etiquetaDocumento}:</Text>
                                <Text style={styles.infoText}>{formatoMoneda(montoOriginal)}</Text>
                            </View>
                            <View>
                                <Text style={styles.infoLabel}>Saldo actual:</Text>
                                <Text style={[styles.infoText, { fontFamily: 'Tw_Cen_MT_Negrita', color: 'red' }]}>
                                    {formatoMoneda(saldoPendienteActual)}
                                </Text>
                            </View>
                        </View>
                    </View>
                </View>

                <View style={styles.tableHeader}>
                    <Text style={styles.col1}>N pago</Text>
                    <Text style={styles.col2}>Fecha Pago</Text>
                    <Text style={styles.col3}>Monto</Text>
                    <Text style={styles.col4}>Saldo Anterior</Text>
                    <Text style={styles.col5}>Saldo Actual</Text>
                </View>

                {historialConSaldos.map((item, index) => {
                    const esAnulado = item.estado === 'Anulada';
                    return (
                        <View key={index} style={styles.tableRow}>
                            <Text style={[styles.col1, esAnulado && styles.datoAnulado]}>{index + 1}</Text>
                            <Text style={[styles.col2, esAnulado && styles.datoAnulado]}>
                                {moment(item.fecha).format('DD/MM/YYYY HH:mm')}
                            </Text>
                            <Text style={[styles.col3, esAnulado && styles.datoAnulado]}>
                                {formatoMoneda(item.monto)}
                            </Text>
                            <Text style={[styles.col4, esAnulado && styles.datoAnulado]}>
                                {formatoMoneda(item.saldoAnterior)}
                            </Text>
                            <Text style={[styles.col5, esAnulado && styles.datoAnulado]}>
                                {formatoMoneda(item.saldoActual)}
                            </Text>
                        </View>
                    );
                })}

                <View style={styles.footerSection}>
                    <View style={styles.leftFooter}>
                        <Text style={styles.infoLabel}>Monto en letras:</Text>
                        <Text style={[styles.infoText, { marginBottom: 15 }]}>{NumeroALetras(credito.saldo)}</Text>

                        <Text style={styles.infoLabel}>Recibido con las formas de pago:</Text>
                        <Text style={styles.infoText}>
                            {Array.from(new Set(historialConSaldos.map(h => h.metodo))).join(', ')}
                        </Text>

                        <View style={{ marginTop: 20 }}>
                            <Text style={styles.infoLabel}>NOTAS ADICIONALES:</Text>
                            <Text style={styles.infoText}>Sin notas</Text>
                        </View>
                    </View>

                    <View style={styles.rightFooter}>
                        <View style={styles.totalRow}>
                            <Text style={styles.infoLabel}>Saldo Inicial:</Text>
                            <Text>{formatoMoneda(montoOriginal)}</Text>
                        </View>
                        <View style={styles.totalRow}>
                            <Text style={styles.infoLabel}>Total Abonado:</Text>
                            <Text>{formatoMoneda(totalAbonado)}</Text>
                        </View>
                        <View style={styles.totalRow}>
                            <Text style={styles.infoLabel}>Monto Pagado:</Text>
                            <Text>{formatoMoneda(totalAbonado)}</Text>
                        </View>
                        <View style={[styles.totalRow, styles.saldoActualLabel]}>
                            <Text>SALDO ACTUAL:</Text>
                            <Text style={{ color: '#D32F2F' }}>{formatoMoneda(saldoPendienteActual)}</Text>
                        </View>
                    </View>
                </View>

                <Text style={{ textAlign: 'center', fontSize: 7, color: '#999', marginTop: 40 }}>
                    Gracias por preferirnos.
                </Text>
            </Page>
        </Document>
    );
};

export default ReporteCredito;